import { useAIChatEditor } from '@alpod-ai-writer/plate-plugins/ai/react';
import { CitationPlugin } from '@alpod-ai-writer/plate-plugins/citations/react';
import { LinkPlugin } from '@alpod-ai-writer/plate-plugins/link/react';
import { ImagePlugin } from '@alpod-ai-writer/plate-plugins/media/react';
import { ShortcutPlugin } from '@alpod-ai-writer/plate-plugins/shortcuts/react';
import { TableCellHeaderPlugin, TableCellPlugin, TablePlugin, TableRowPlugin } from '@alpod-ai-writer/plate-plugins/table/react';
import { withProps } from '@udecode/cn';
import { type SlateElementProps } from '@udecode/plate';
import { ParagraphPlugin, PlateElement, PlateLeaf, usePlateEditor } from '@udecode/plate/react';
import { AlignPlugin } from '@udecode/plate-alignment/react';
import { BasicMarksPlugin, BoldPlugin, ItalicPlugin, UnderlinePlugin } from '@udecode/plate-basic-marks/react';
import { CaptionPlugin } from '@udecode/plate-caption/react';
import { HEADING_KEYS } from '@udecode/plate-heading';
import { HeadingPlugin } from '@udecode/plate-heading/react';
import { BulletedListPlugin, ListItemPlugin, ListPlugin, NumberedListPlugin } from '@udecode/plate-list/react';
import { MarkdownPlugin } from '@udecode/plate-markdown';
import { memo } from 'react';

import { CitationElement } from '../elements/citationElement.component';
import { ImageElement } from '../elements/imageElement.component';
import { LinkElementStatic } from '../elements/linkElementStatic.component';
import { ShortcutElement } from '../elements/shortcutElement.component';
import { TableCellElementStatic, TableCellHeaderStaticElement } from '../elements/tableCellElementStatic.component';
import { TableElementStatic } from '../elements/tableElementStatic.component';
import { TableRowElementStatic } from '../elements/tableRowElementStatic.component';
import { EditorStatic } from './editorStatic.component';

const components = {
    [BoldPlugin.key]: withProps(PlateLeaf, { as: 'strong' }),
    [ItalicPlugin.key]: withProps(PlateLeaf, { as: 'em' }),
    [UnderlinePlugin.key]: withProps(PlateLeaf, { as: 'u' }),
    [ParagraphPlugin.key]: withProps(PlateElement, {
        as: 'p',
        className: 'mb-4 leading-relaxed text-justify',
    }),
    [HEADING_KEYS.h1]: ({ attributes, children, element }: SlateElementProps) => <h1 id={element.id as string} {...attributes} className='text-3xl font-bold mb-4 mt-10 first:mt-0'>{children}</h1>,
    [HEADING_KEYS.h2]: ({ attributes, children, element }: SlateElementProps) => <h2 id={element.id as string} {...attributes} className='text-2xl font-semibold mb-2 mt-8'>{children}</h2>,
    [HEADING_KEYS.h3]: ({ attributes, children, element }: SlateElementProps) => <h3 id={element.id as string} {...attributes} className='text-xl font-bold mb-2 mt-6'>{children}</h3>,
    [BulletedListPlugin.key]: withProps(PlateElement, { as: 'ul', className: 'list-disc pl-6 mb-4 mt-2' }),
    [NumberedListPlugin.key]: withProps(PlateElement, { as: 'ol', className: 'list-decimal pl-6 mb-4 mt-2' }),
    [ListItemPlugin.key]: withProps(PlateElement, { as: 'li', className: 'mb-2' }),
    [CitationPlugin.key]: CitationElement,
    [ShortcutPlugin.key]: ShortcutElement,
    [ImagePlugin.key]: ImageElement,
    [TableCellHeaderPlugin.key]: TableCellHeaderStaticElement,
    [TableCellPlugin.key]: TableCellElementStatic,
    [TablePlugin.key]: TableElementStatic,
    [TableRowPlugin.key]: TableRowElementStatic,
    [LinkPlugin.key]: LinkElementStatic
};

const plugins = [
    HeadingPlugin.configure({ options: { levels: 3 } }),
    BasicMarksPlugin,
    CitationPlugin.configure({ options: { insertSpaceAfterCitation: true, trigger: '@' } }),
    ShortcutPlugin.configure({ options: { insertSpaceAfterShortcut: true, trigger: '$' } }),
    AlignPlugin.configure({
        inject: {
            targetPlugins: [
                ParagraphPlugin.key,
                HeadingPlugin.key,
                ImagePlugin.key,
                CaptionPlugin.key,
            ],
            nodeProps: {
                defaultNodeValue: 'justify',
            },
        },
    }),
    ListPlugin,
    MarkdownPlugin.configure({ options: { indentList: true } }),
];

export const AIChatEditor = memo(({ content }: { content: string }) => {
    const aiEditor = usePlateEditor({
        plugins,
    });

    useAIChatEditor(aiEditor, content);

    return (
        <div
            className="max-h-[min(70vh,320px)] overflow-y-auto border-b"
            onWheel={(e) => e.stopPropagation()}
        >
            <EditorStatic variant="aiChat" components={components} editor={aiEditor} />
        </div>
    );
});
